/**
 * Matsubishi Favorites Manager
 * localStorage as primary storage, Storefront API for cross-device sync
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'matsubishi_favorites';
  const PRODUCTS_KEY = 'matsubishi_favorites_products';
  const TOKEN_KEY = 'matsubishi_customer_token';

  const isProduction = () => window.location.host === 'matsubishi.online';
  const getShopifyDomain = () =>
    isProduction()
      ? '029fc7-3f.myshopify.com'
      : 'xn-k73bqgqqybh1j7t9c9711e9kxa46al24t3rua.myshopify.com';
  const getStorefrontToken = () =>
    isProduction()
      ? '955e8cfddd19564401a58b16f6f3ab53'
      : 'd6685e69bf57bc779dfe426e591633bf';

  function storefrontFetch(query, variables) {
    return fetch(`https://${getShopifyDomain()}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': getStorefrontToken(),
      },
      body: JSON.stringify({ query, variables }),
    }).then((r) => r.json());
  }

  function getCustomerToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  function setCustomerToken(token) {
    localStorage.setItem(TOKEN_KEY, token);
  }

  // Handles list (array of product handles)
  function getHandles() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function setHandles(handles) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  }

  // Product data cache (map of handle → product info)
  function getProductsCache() {
    try {
      const raw = localStorage.getItem(PRODUCTS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function setProductsCache(cache) {
    localStorage.setItem(PRODUCTS_KEY, JSON.stringify(cache));
  }

  function cacheProduct(handle, data) {
    const cache = getProductsCache();
    cache[handle] = data;
    setProductsCache(cache);
  }

  // Sync favorites handles to customer metafields via Storefront API
  async function syncToServer(handles) {
    const token = getCustomerToken();
    if (!token) return;

    const mutation = `
      mutation customerUpdate($customerAccessToken: String!, $customer: CustomerUpdateInput!) {
        customerUpdate(customerAccessToken: $customerAccessToken, customer: $customer) {
          customerUserErrors { message }
        }
      }
    `;

    try {
      const result = await storefrontFetch(mutation, {
        customerAccessToken: token,
        customer: {
          metafields: [
            {
              namespace: 'custom',
              key: 'favorites',
              value: JSON.stringify(handles),
              type: 'json',
            },
          ],
        },
      });
      const errors = result?.data?.customerUpdate?.customerUserErrors;
      if (errors && errors.length > 0) {
        localStorage.removeItem(TOKEN_KEY);
      }
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
    }
  }

  function isFavorited(handle) {
    return getHandles().includes(handle);
  }

  function updateButtonState(btn, favorited) {
    if (favorited) {
      btn.classList.add('is-favorited');
      btn.setAttribute('aria-pressed', 'true');
      btn.setAttribute('aria-label', 'お気に入りから削除');
    } else {
      btn.classList.remove('is-favorited');
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-label', 'お気に入りに追加');
    }
  }

  function updateAllButtons(handle, favorited) {
    document.querySelectorAll(`[data-fav-handle="${handle}"]`).forEach((btn) => {
      updateButtonState(btn, favorited);
    });
  }

  async function toggleFavorite(btn) {
    const handle = btn.getAttribute('data-fav-handle');
    let handles = getHandles();
    const idx = handles.indexOf(handle);
    let favorited;

    if (idx === -1) {
      handles.push(handle);
      favorited = true;

      // Cache product data from button attributes
      const productData = {
        handle,
        title: btn.getAttribute('data-fav-title') || '',
        image: btn.getAttribute('data-fav-image') || '',
        price: btn.getAttribute('data-fav-price') || '',
        url: btn.getAttribute('data-fav-url') || ('/products/' + handle),
      };
      cacheProduct(handle, productData);
    } else {
      handles.splice(idx, 1);
      favorited = false;
    }

    setHandles(handles);
    updateAllButtons(handle, favorited);
    await syncToServer(handles);

    return favorited;
  }

  // ---- Favorites page rendering ----

  function esc(str) {
    return String(str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function formatPrice(price) {
    if (!price) return '';
    const num = Math.round(parseInt(price, 10) / 100);
    if (isNaN(num)) return '';
    return '¥' + num.toLocaleString('ja-JP');
  }

  async function fetchVariantId(handle) {
    try {
      const res = await fetch('/products/' + handle + '.js');
      if (!res.ok) return null;
      const product = await res.json();
      const variants = product.variants || [];
      // 先頭固定だと先頭バリアントの在庫切れで購入可能な商品もカート不可になる
      const variant = variants.find((v) => v.available) || variants[0];
      if (!variant) return null;
      return { id: variant.id, available: variant.available };
    } catch (e) {
      return null;
    }
  }

  function renderFavoritesPage() {
    const container = document.getElementById('favorites-content');
    if (!container) return;

    const handles = getHandles();
    const cache = getProductsCache();

    if (handles.length === 0) {
      container.innerHTML =
        '<div class="favorites-empty">' +
          '<p class="favorites-empty__title">お気に入りはまだありません</p>' +
          '<a class="favorites-empty__link" href="/collections/kani">商品一覧を見る</a>' +
        '</div>';
      return;
    }

    // Render cards from cache (may fetch variant IDs async)
    const listItems = handles.map((handle) => {
      const p = cache[handle] || { handle, title: handle, image: '', price: '', url: '/products/' + handle };
      return (
        '<li class="favorites-card" data-handle="' + esc(handle) + '">' +
          '<a class="favorites-card__image-link" href="' + esc(p.url) + '">' +
            (p.image
              ? '<img class="favorites-card__image" src="' + esc(p.image) + '" alt="' + esc(p.title) + '" width="80" height="80" loading="lazy">'
              : '<div class="favorites-card__image favorites-card__image--placeholder"></div>') +
          '</a>' +
          '<div class="favorites-card__info">' +
            '<a class="favorites-card__title" href="' + esc(p.url) + '">' + esc(p.title) + '</a>' +
            (p.price ? '<span class="favorites-card__price">' + formatPrice(p.price) + '</span>' : '') +
          '</div>' +
          '<div class="favorites-card__actions">' +
            '<div class="favorites-card__qty-wrap">' +
              '<button class="favorites-card__qty-btn" type="button" data-qty-delta="-1" aria-label="数量を減らす">−</button>' +
              '<span class="favorites-card__qty-value">1</span>' +
              '<button class="favorites-card__qty-btn" type="button" data-qty-delta="1" aria-label="数量を増やす">＋</button>' +
            '</div>' +
            '<button class="favorites-card__add-to-cart" data-handle="' + esc(handle) + '" disabled>読み込み中...</button>' +
            '<button class="favorites-card__remove fav-btn is-favorited"' +
              ' data-fav-handle="' + esc(handle) + '"' +
              ' data-fav-title="' + esc(p.title) + '"' +
              ' data-fav-image="' + esc(p.image) + '"' +
              ' data-fav-price="' + esc(p.price) + '"' +
              ' data-fav-url="' + esc(p.url) + '"' +
              ' aria-label="お気に入りから削除" aria-pressed="true">' +
              '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
                '<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>' +
              '</svg>' +
            '</button>' +
          '</div>' +
        '</li>'
      );
    }).join('');

    container.innerHTML = '<ul class="favorites-list" role="list">' + listItems + '</ul>';

    // Async: fetch variant IDs and enable add-to-cart buttons
    handles.forEach(async (handle) => {
      const variant = await fetchVariantId(handle);
      const card = container.querySelector('.favorites-card[data-handle="' + handle + '"]');
      if (!card) return;
      const addBtn = card.querySelector('.favorites-card__add-to-cart');
      if (!addBtn) return;

      if (variant) {
        addBtn.setAttribute('data-variant-id', variant.id);
        if (variant.available) {
          addBtn.disabled = false;
          addBtn.textContent = 'カートに入れる';
        } else {
          addBtn.disabled = true;
          addBtn.textContent = '売り切れ';
        }
      } else {
        addBtn.disabled = true;
        addBtn.textContent = '情報取得失敗';
      }
    });

    // Remove card when unfavorited
    container.querySelectorAll('.favorites-card__remove').forEach((btn) => {
      btn.addEventListener('click', async function () {
        const handle = this.getAttribute('data-fav-handle');
        const favorited = await toggleFavorite(this);
        if (favorited === false) {
          const card = this.closest('.favorites-card');
          if (card) {
            card.style.transition = 'opacity 0.3s';
            card.style.opacity = '0';
            setTimeout(() => {
              card.remove();
              const list = container.querySelector('.favorites-list');
              if (list && list.querySelectorAll('.favorites-card').length === 0) {
                renderFavoritesPage();
              }
            }, 300);
          }
        }
      });
    });
  }

  // ---- Setup all fav buttons on the page ----

  function setupFavButtons() {
    const handles = getHandles();
    document.querySelectorAll('[data-fav-handle]').forEach((btn) => {
      const handle = btn.getAttribute('data-fav-handle');
      updateButtonState(btn, handles.includes(handle));
    });
  }

  // ---- Global click handler for non-favorites-page buttons ----

  function setupGlobalClickHandler() {
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-fav-handle]');
      if (!btn) return;
      if (btn.classList.contains('favorites-card__remove')) return; // handled separately
      if (document.getElementById('favorites-content')) return; // handled by renderFavoritesPage

      e.preventDefault();
      toggleFavorite(btn);
    });
  }

  // ---- Login interceptor: capture customer token on login ----

  function setupLoginInterceptor() {
    const loginForm = document.querySelector('form[action="/account/login"]');
    if (!loginForm) return;

    loginForm.addEventListener('submit', function () {
      const emailInput = loginForm.querySelector('[name="customer[email]"]');
      const passwordInput = loginForm.querySelector('[name="customer[password]"]');
      if (!emailInput || !passwordInput) return;

      const email = emailInput.value;
      const password = passwordInput.value;
      if (!email || !password) return;

      const mutation = `
        mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
          customerAccessTokenCreate(input: $input) {
            customerAccessToken { accessToken }
            customerUserErrors { message }
          }
        }
      `;

      storefrontFetch(mutation, { input: { email, password } })
        .then((data) => {
          const token =
            data?.data?.customerAccessTokenCreate?.customerAccessToken?.accessToken;
          if (token) setCustomerToken(token);
        })
        .catch(() => {});
    });
  }

  // ---- Add to cart from favorites page ----

  function setupAddToCart() {
    // Quantity +/- buttons
    document.addEventListener('click', function (e) {
      const qtyBtn = e.target.closest('.favorites-card__qty-btn');
      if (!qtyBtn) return;
      const delta = parseInt(qtyBtn.getAttribute('data-qty-delta'), 10);
      const valueEl = qtyBtn.closest('.favorites-card__qty-wrap').querySelector('.favorites-card__qty-value');
      const current = parseInt(valueEl.textContent, 10) || 1;
      valueEl.textContent = Math.max(1, Math.min(99, current + delta));
    });

    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.favorites-card__add-to-cart');
      if (!btn || btn.disabled) return;

      e.preventDefault();
      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      const qtyEl = btn.closest('.favorites-card__actions')?.querySelector('.favorites-card__qty-value');
      const quantity = Math.max(1, parseInt(qtyEl?.textContent, 10) || 1);

      btn.disabled = true;
      btn.textContent = '追加中...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity }),
      })
        .then((r) => {
          if (!r.ok) throw new Error('cart add failed: ' + r.status);
          return r.json();
        })
        .then(() => {
          btn.textContent = 'カートに追加しました';
          setTimeout(() => {
            btn.disabled = false;
            btn.textContent = 'カートに入れる';
          }, 2000);
        })
        .catch(() => {
          btn.textContent = '追加できませんでした';
          setTimeout(() => {
            btn.disabled = false;
            btn.textContent = 'カートに入れる';
          }, 2000);
        });
    });
  }

  // theme.liquid が出力する顧客メタフィールド由来のお気に入りを取り込む
  // (これまで書き込み専用で、別デバイスのお気に入りが反映されなかった)
  function mergeServerFavorites() {
    const server = window.matsubishiFavorites;
    if (!server || !Array.isArray(server.handles) || server.handles.length === 0) return;
    const local = getHandles();
    const merged = Array.from(new Set(local.concat(server.handles)));
    if (merged.length !== local.length) {
      setHandles(merged);
    }
  }

  function init() {
    mergeServerFavorites();
    setupFavButtons();
    if (document.getElementById('favorites-content')) {
      renderFavoritesPage();
    }
    setupGlobalClickHandler();
    setupLoginInterceptor();
    setupAddToCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
